import { useDeferredValue } from 'react';
import type { RefObject } from 'react';
import { Body, InlineAlert } from '@e2n/ui/v2';
import { useTranslation } from 'react-i18next';
import { useAutoScroll } from '../hooks/useAutoScroll';
import { useChatStore } from '../hooks/useChatStore';
import type { ChatMessage } from '../hooks/useChatStore';
import { MarkdownText } from './MarkdownText';

export function ChatMessages({
  scrollRef,
}: {
  scrollRef: RefObject<HTMLElement | null>;
}) {
  const { t } = useTranslation();
  const messages = useChatStore((state) => state.messages);
  const answer = useChatStore((state) => state.answer);
  const isStreaming = useChatStore((state) => state.isStreaming);
  const error = useChatStore((state) => state.error);
  const deferredAnswer = useDeferredValue(answer);

  useAutoScroll(scrollRef, isStreaming);

  return (
    <div className="flex flex-col gap-6 pb-4">
      {messages.map((message) => (
        <Message key={message.id} message={message} />
      ))}
      {isStreaming && !answer && <ChatStatus />}
      {deferredAnswer && <MarkdownText text={deferredAnswer} />}
      {error && (
        <InlineAlert
          size="sm"
          description={t(`domain.ai.chat.errors.${error}`)}
        />
      )}
    </div>
  );
}

function Message({ message }: { message: ChatMessage }) {
  if (message.role === 'AGENT') {
    return <MarkdownText text={message.text} />;
  }
  return (
    <Body className="ml-auto max-w-70 rounded-xl bg-neutrals-v2-200 p-3 whitespace-pre-wrap">
      {message.text}
    </Body>
  );
}

function ChatStatus() {
  const { t } = useTranslation();

  return (
    <div role="status" className="flex items-center gap-2">
      <span aria-hidden className="flex gap-1">
        <span className="size-1.5 animate-pulse rounded-full bg-neutrals-v2-500" />
        <span className="size-1.5 animate-pulse rounded-full bg-neutrals-v2-500 [animation-delay:200ms]" />
        <span className="size-1.5 animate-pulse rounded-full bg-neutrals-v2-500 [animation-delay:400ms]" />
      </span>
      <Body>{t('domain.ai.chat.status.thinking')}</Body>
    </div>
  );
}
